import React, {useEffect, useRef, useState} from 'react';
import {Alert, Button, Form, Modal, Spinner} from 'react-bootstrap';
import {useNavigate} from 'react-router-dom';
import Title from "@UIComponents/Title/Title";
import refundTransaction, {fetchRefundStatus, refundAnrokOnly} from "@reduxServices/refundTransaction";
import {GAMES_PATH} from "@constants/Paths";
import {getAccessState} from "@utils/helpers/access";
import styles from './refunds.module.css';

const COMMENT_PATTERN = /^[a-zA-Z0-9\s-']+$/;
const COMPLETED_STATUS = 'COMPLETED';
const ANROK_ONLY_OPERATION = 'ANROK_ONLY';
const POLL_DELAY_MS = 5000;
const POLLING_WINDOW_MS = 30000;

export const validateRefundForm = ({transactionId = '', comment = ''}) => {
    const normalizedTransactionId = transactionId.trim();
    const errors = {};

    if (!normalizedTransactionId) errors.transactionId = 'Transaction ID is required.';
    if (!comment.trim()) {
        errors.comment = 'Comment is required.';
    } else if (comment.length > 200) {
        errors.comment = 'Comment must be 200 characters or fewer.';
    } else if (!COMMENT_PATTERN.test(comment)) {
        errors.comment = "Comment may contain only letters, numbers, spaces, hyphens and apostrophes.";
    }

    return {
        values: {transactionId: normalizedTransactionId, comment},
        errors
    };
};

const getRequestError = (error) => {
    if (!error.response) {
        return {
            type: 'network',
            message: 'The result of the financial operation is unknown. Check the provider before deciding whether to retry.'
        };
    }

    const responseData = error.response.data;
    return {
        type: 'api',
        status: error.response.status,
        code: responseData?.error,
        message: responseData?.message || 'The refund request was rejected. Review the details and try again manually if appropriate.'
    };
};

const getStatusError = (error) => {
    if (!error.response || error.response.status >= 500) {
        return {
            type: 'temporary',
            message: 'The status update failed temporarily. The last known status is shown. Check again manually.'
        };
    }

    const requestError = getRequestError(error);
    const status = error.response.status;
    const responseData = error.response.data;

    if (status === 404 && responseData?.error === 'REFUND_NOT_FOUND') {
        return {
            ...requestError,
            type: 'not-found',
            message: 'The refund record was not found. Verify the transaction ID before trying again.'
        };
    }

    return requestError;
};

const displayValue = value => value === undefined || value === null || value === '' ? '—' : String(value);

const getStatusMessage = result => {
    if (result.overallStatus === 'TAX_REFUND_COMPLETED') {
        return `Tax refund completed in Anrok. Payment refund status remains ${displayValue(result.payment?.status)}.`;
    }
    if (result.anrok?.status === COMPLETED_STATUS && result.overallStatus !== COMPLETED_STATUS) {
        return `Refund completed in Anrok only. Payment refund status remains ${displayValue(result.payment?.status)}.`;
    }

    switch (result.overallStatus) {
        case 'PROCESSING':
            return 'The refund is still being processed. No automatic retry was performed.';
        case 'COMPLETED':
            return 'Refund completed successfully.';
        case 'PAYMENT_REFUND_COMPLETED':
            return 'Payment refund completed, but the Anrok stage is unfinished.';
        case 'FAILED':
            return 'Refund failed. Review the stage and reason before retrying.';
        case 'PENDING':
        case 'ACCEPTED':
            return 'The payment provider has not returned a final result yet.';
        case 'MANUAL_REQUIRED':
            return 'Manual processing is required. No automatic retry was performed.';
        case 'UNKNOWN_REQUIRES_RECONCILIATION':
            return 'The external financial call has an unknown result. The refund may have been executed.';
        default:
            return 'The backend returned a result that requires operator review.';
    }
};

const displayError = error => typeof error === 'string' ? error : JSON.stringify(error);

const formatTimestamp = value => {
    if (value === undefined || value === null || value === '') return '—';

    const timestamp = Number(value);
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? displayValue(value) : date.toLocaleString();
};

const ProviderResult = ({title, data = {}, fields}) => (
    <div className={styles.providerResult}>
        <h3>{title}</h3>
        <dl>
            {fields.map(({label, key}) => (
                <React.Fragment key={key}>
                    <dt>{label}</dt>
                    <dd>{displayValue(data[key])}</dd>
                </React.Fragment>
            ))}
        </dl>
    </div>
);

const RefundResult = ({result}) => {
    const errors = Array.isArray(result.errors) ? result.errors : [];
    const payment = result.payment || {};
    const anrok = result.anrok || {};

    const alertVariant = result.overallStatus === COMPLETED_STATUS
        ? 'success'
        : result.overallStatus === 'FAILED' ? 'danger' : 'warning';

    return (
        <section className={styles.result} aria-live="polite">
            <Alert variant={alertVariant}>
                <strong>{displayValue(result.overallStatus)}</strong>
                <div>{getStatusMessage(result)}</div>
            </Alert>
            <dl className={styles.summary}>
                <dt>Refund ID</dt>
                <dd>{displayValue(result.refundId)}</dd>
                <dt>Transaction ID</dt>
                <dd>{displayValue(result.transactionId)}</dd>
                <dt>Purchase request ID</dt>
                <dd>{displayValue(result.purchaseRequestId)}</dd>
                <dt>Payment provider</dt>
                <dd>{displayValue(result.paymentProvider)}</dd>
                <dt>Created at</dt>
                <dd>{formatTimestamp(result.createdAt)}</dd>
                <dt>Updated at</dt>
                <dd>{formatTimestamp(result.updatedAt)}</dd>
            </dl>
            <div className={styles.providers}>
                <ProviderResult
                    title="Payment"
                    data={payment}
                    fields={[
                        {label: 'Status', key: 'status'},
                        {label: 'Provider status', key: 'providerStatus'},
                        {label: 'Provider reference ID', key: 'providerReferenceId'},
                        {label: 'HTTP status', key: 'httpStatus'},
                        {label: 'Error code', key: 'errorCode'},
                        {label: 'Error message', key: 'errorMessage'}
                    ]}
                />
                <ProviderResult
                    title="Anrok"
                    data={anrok}
                    fields={[
                        {label: 'Status', key: 'status'},
                        {label: 'Provider status', key: 'providerStatus'},
                        {label: 'Provider reference ID', key: 'providerReferenceId'},
                        {label: 'HTTP status', key: 'httpStatus'},
                        {label: 'Error code', key: 'errorCode'},
                        {label: 'Error message', key: 'errorMessage'}
                    ]}
                />
            </div>
            {errors.length > 0 &&
                <Alert variant="danger">
                    <h3>Errors</h3>
                    <ul className={styles.errors}>
                        {errors.map((error, index) => <li key={index}>{displayError(error)}</li>)}
                    </ul>
                </Alert>
            }
        </section>
    );
};

const RefundsPage = () => {
    const navigate = useNavigate();
    const [form, setForm] = useState({transactionId: '', comment: ''});
    const [validationErrors, setValidationErrors] = useState({});
    const [result, setResult] = useState(null);
    const [requestError, setRequestError] = useState(null);
    const [statusError, setStatusError] = useState(null);
    const [confirmationAction, setConfirmationAction] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isPolling, setIsPolling] = useState(false);
    const [pollingWindowExpired, setPollingWindowExpired] = useState(false);
    const submissionStarted = useRef(false);
    const pollTimeout = useRef(null);
    const pollingWindowTimeout = useRef(null);
    const pollGeneration = useRef(0);
    const statusRequestInFlight = useRef(false);
    const isMounted = useRef(false);
    const {hasGamesAccess} = getAccessState();

    const completed = result?.terminal === true && result?.overallStatus === COMPLETED_STATUS;
    const canStartAnotherRefund = completed || pollingWindowExpired;
    const fieldsDisabled = isSubmitting || isPolling || completed || pollingWindowExpired;
    const isConfirmationOpen = confirmationAction !== null;
    const isAnrokOnly = confirmationAction === ANROK_ONLY_OPERATION;

    const clearPolling = () => {
        if (pollTimeout.current !== null) {
            clearTimeout(pollTimeout.current);
            pollTimeout.current = null;
        }
        if (pollingWindowTimeout.current !== null) {
            clearTimeout(pollingWindowTimeout.current);
            pollingWindowTimeout.current = null;
        }
        pollGeneration.current += 1;
        statusRequestInFlight.current = false;
    };

    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;
            clearPolling();
        };
    }, []);

    const stopPolling = () => {
        clearPolling();
        setIsPolling(false);
    };

    const handleStatusResponse = (response, transactionId, generation) => {
        if (pollGeneration.current !== generation) return;

        setResult(response);
        setStatusError(null);

        if (response.terminal === false) {
            setIsPolling(true);
            pollTimeout.current = setTimeout(() => {
                pollTimeout.current = null;
                runStatusCheck(transactionId, generation);
            }, POLL_DELAY_MS);
        } else {
            stopPolling();
        }
    };

    const handleStatusFailure = error => {
        setStatusError(getStatusError(error));
        stopPolling();
    };

    const runStatusCheck = async (transactionId, generation) => {
        if (pollGeneration.current !== generation || statusRequestInFlight.current) return;

        statusRequestInFlight.current = true;
        setIsPolling(true);

        try {
            const response = await fetchRefundStatus(transactionId);
            handleStatusResponse(response, transactionId, generation);
        } catch (error) {
            if (pollGeneration.current === generation) handleStatusFailure(error);
        } finally {
            if (pollGeneration.current === generation) statusRequestInFlight.current = false;
        }
    };

    const startPolling = (transactionId, checkImmediately = false) => {
        clearPolling();
        const generation = pollGeneration.current;
        setStatusError(null);
        setPollingWindowExpired(false);
        setIsPolling(true);
        pollingWindowTimeout.current = setTimeout(() => {
            if (pollGeneration.current !== generation) return;

            stopPolling();
            if (isMounted.current) setPollingWindowExpired(true);
        }, POLLING_WINDOW_MS);
        if (checkImmediately) {
            runStatusCheck(transactionId, generation);
        } else {
            pollTimeout.current = setTimeout(() => {
                pollTimeout.current = null;
                runStatusCheck(transactionId, generation);
            }, POLL_DELAY_MS);
        }
    };

    const checkStatusNow = () => {
        const transactionId = result?.transactionId || form.transactionId.trim();
        if (!transactionId || statusRequestInFlight.current) return;

        startPolling(transactionId, true);
    };

    const handleChange = ({target: {name, value}}) => {
        if (fieldsDisabled) return;

        setForm(current => ({...current, [name]: value}));
        setValidationErrors(current => ({...current, [name]: undefined}));
        setResult(null);
        setRequestError(null);
        setStatusError(null);
    };

    const openConfirmation = operation => {
        const validation = validateRefundForm(form);
        setForm(validation.values);
        setValidationErrors(validation.errors);

        if (Object.keys(validation.errors).length === 0) {
            submissionStarted.current = false;
            setConfirmationAction(operation);
        }
    };

    const validateAndOpenConfirmation = event => {
        event.preventDefault();
        openConfirmation('FULL');
    };

    const confirmRefund = async () => {
        if (!confirmationAction || submissionStarted.current) return;

        const operation = confirmationAction;
        submissionStarted.current = true;
        stopPolling();
        setConfirmationAction(null);
        setIsSubmitting(true);
        setRequestError(null);
        setStatusError(null);
        setPollingWindowExpired(false);

        try {
            const response = operation === ANROK_ONLY_OPERATION
                ? await refundAnrokOnly(form)
                : await refundTransaction(form);
            if (!isMounted.current) return;
            setResult(response);
            if (response.terminal === false) startPolling(response.transactionId || form.transactionId);
        } catch (error) {
            if (isMounted.current) setRequestError(getRequestError(error));
        } finally {
            if (isMounted.current) setIsSubmitting(false);
            submissionStarted.current = false;
        }
    };

    const resetForm = () => {
        stopPolling();
        setForm({transactionId: '', comment: ''});
        setValidationErrors({});
        setResult(null);
        setRequestError(null);
        setStatusError(null);
        setPollingWindowExpired(false);
        submissionStarted.current = false;
    };

    const actionLabel = result?.overallStatus === 'PAYMENT_REFUND_COMPLETED'
        ? 'Continue unfinished stage'
        : result?.overallStatus
            ? 'Retry refund manually'
            : 'Run full refund';

    return (
        <main className="mainContent">
            <div className={styles.page}>
                <div className="title-wrapper">
                    <Title mainText="Refunds"/>
                    {hasGamesAccess &&
                        <Button variant="outline-secondary" onClick={() => navigate(GAMES_PATH)}>
                            Back to Games
                        </Button>
                    }
                </div>
                <hr className="horizontal-line"/>

                <section className={styles.formSection}>
                    <p className={styles.description}>
                        Run a full refund for a single transaction. Partial refunds are not supported.
                    </p>
                    <Form onSubmit={validateAndOpenConfirmation} noValidate>
                        <Form.Group className="mb-3" controlId="transactionId">
                            <Form.Label className="required">Transaction ID</Form.Label>
                            <Form.Control
                                name="transactionId"
                                value={form.transactionId}
                                onChange={handleChange}
                                disabled={fieldsDisabled}
                                isInvalid={!!validationErrors.transactionId}
                                autoComplete="off"
                            />
                            <Form.Control.Feedback type="invalid">
                                {validationErrors.transactionId}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="comment">
                            <Form.Label className="required">Comment</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="comment"
                                value={form.comment}
                                onChange={handleChange}
                                disabled={fieldsDisabled}
                                isInvalid={!!validationErrors.comment}
                                maxLength={200}
                                pattern={"^[a-zA-Z0-9\\s-']+$"}
                            />
                            <Form.Text>{form.comment.length}/200</Form.Text>
                            <Form.Control.Feedback type="invalid">
                                {validationErrors.comment}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <div className={styles.actions}>
                            {canStartAnotherRefund ?
                                <Button variant="primary" onClick={resetForm}>New refund</Button> :
                                <>
                                    <Button type="submit" variant="primary" disabled={isSubmitting || isPolling}>
                                        {isSubmitting
                                            ? <><Spinner animation="border" size="sm" aria-hidden="true"/> Starting refund...</>
                                            : isPolling
                                                ? <><Spinner animation="border" size="sm" aria-hidden="true"/> Checking refund status...</>
                                                : actionLabel}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline-secondary"
                                        disabled={isSubmitting || isPolling || !form.transactionId.trim()}
                                        onClick={checkStatusNow}
                                    >
                                        Check saved status
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline-danger"
                                        disabled={isSubmitting || isPolling}
                                        onClick={() => openConfirmation(ANROK_ONLY_OPERATION)}
                                    >
                                        Refund in Anrok only
                                    </Button>
                                </>
                            }
                        </div>
                    </Form>
                </section>

                {isPolling && result?.terminal === false &&
                    <Alert variant="info" className={styles.requestError}>
                        <div>Refund processing is in progress. The system is checking the saved refund record in our database every 5 seconds.</div>
                        <div>This does not repeat the financial refund request or call Amazon Pay. For asynchronous providers, including Amazon Pay, the provider callback/IPN updates the saved record automatically.</div>
                        <div>You may close this page safely. Closing it does not change the refund. To check later, enter the same Transaction ID to load the current saved status.</div>
                    </Alert>
                }

                {pollingWindowExpired && result?.terminal === false &&
                    <Alert variant="info" className={styles.requestError}>
                        <div>The saved refund status did not change during 30 seconds of polling. The provider callback/IPN can still update it after this page is closed.</div>
                        <div>Click New refund to process another transaction. To check this refund later, enter the same Transaction ID to load its current saved status.</div>
                    </Alert>
                }

                {requestError &&
                    <Alert variant="danger" className={styles.requestError}>
                        {requestError.status && <strong>Request failed ({requestError.status}). </strong>}
                        {requestError.code && <strong>{requestError.code}: </strong>}
                        {requestError.message}
                    </Alert>
                }

                {statusError &&
                    <Alert variant={statusError.type === 'temporary' ? 'warning' : 'danger'} className={styles.requestError}>
                        {statusError.status && <strong>Status check failed ({statusError.status}). </strong>}
                        {statusError.code && <strong>{statusError.code}: </strong>}
                        {statusError.message}
                        {statusError.type === 'temporary' && result?.terminal === false &&
                            <div className="mt-2">
                                <Button variant="outline-secondary" onClick={checkStatusNow} disabled={isPolling}>
                                    Check status again
                                </Button>
                            </div>
                        }
                    </Alert>
                }

                {result && <RefundResult result={result}/>}
            </div>

            <Modal
                centered
                show={isConfirmationOpen}
                onHide={() => !isSubmitting && setConfirmationAction(null)}
                backdrop="static"
                keyboard={!isSubmitting}
            >
                <Modal.Header closeButton={!isSubmitting}>
                    <Modal.Title>{isAnrokOnly ? 'Confirm Anrok-only refund' : 'Confirm full refund'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p><strong>Transaction ID:</strong> {form.transactionId}</p>
                    <p><strong>Comment:</strong> {form.comment}</p>
                    <Alert variant="warning" className="mb-0">
                        {isAnrokOnly
                            ? 'Confirm that the full payment refund was already completed in the payment provider. Store Admin will not call the payment provider and will refund only in Anrok.'
                            : 'This will execute a full refund. The operation has financial consequences.'}
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        disabled={isSubmitting}
                        onClick={() => setConfirmationAction(null)}
                    >
                        Cancel
                    </Button>
                    <Button variant="danger" disabled={isSubmitting} onClick={confirmRefund}>
                        {isSubmitting ? <Spinner animation="border" size="sm"/> : isAnrokOnly ? 'Confirm Anrok-only refund' : 'Confirm refund'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </main>
    );
};

export default RefundsPage;
